import { useEffect, useState } from 'react';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { Label } from '../../clients/quipli-service/label/label';
import { Typography } from '../../components/typography/typography';
import { useCustomer } from '../../hooks/use-customer-context';
import { CheckoutStep } from './checkout-step';
import { StepAction } from './checkout-view';
import { DatabaseUserDocumentType, UserDocument, UserDocumentType } from '../../clients/quipli-service/user/user';
import { InsuranceFormWrapper } from '../insurance/insurance-wrapper';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { LicenseWrapper } from '../license/license-wrapper';
import { EcommerceButton } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import { LegacyDownloadLink } from '../../components/link/legacy-download-link';
import { DateTime } from 'luxon';
import { FileField } from '../../components/field/field';
import { Divider } from '../../components/divider/divider';
import { Spinner } from '../../components/spinner/spinner';
import { ecommerceToast } from '../../util/toasts';
import { Card } from '../../components/card/card';

export const CheckoutDocuments = ({
  currentStep,
  step,
  conf,
  labels,
  isRental,
  categoryIds,
  isSubmitting,
  setCurrentStep,
  setVisibleDocuments,
  isInsuranceRequiredAtProductLevel = false,
}: {
  currentStep: StepAction;
  step: number;
  conf: Configuration[];
  labels: Label[];
  isRental: boolean;
  categoryIds: number[];
  isSubmitting: boolean;
  setCurrentStep: (step: StepAction) => void;
  setVisibleDocuments: (docs: UserDocumentType[]) => void;
  isInsuranceRequiredAtProductLevel?: boolean;
}) => {
  const customer = useCustomer();
  const { getMultipleCategoriesByIds, getUserDocuments, getUserDriverLicense, getInsuranceDocuments } =
    useCustomerAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showLicense, setShowLicense] = useState<boolean>(false);
  const [showInsurance, setShowInsurance] = useState<boolean>(false);
  const [showOther, setShowOther] = useState<boolean>(false);

  const isLicenseVisible = async (licenseFile?: UserDocument): Promise<boolean> => {
    // If license requirement is not enabled, we don't show the license form
    const licenseEnabled = conf.find((c) => c.variable === 'CONF_RENTER_LIC_INFO')?.value === '1';
    if (!licenseEnabled) {
      return false;
    }

    // If all categories don't require license, we don't show the license form
    const categories = await getMultipleCategoriesByIds(categoryIds);
    if (categories.length > 0 && categories.every((cat) => !cat.requiresLicense)) {
      return false;
    }

    // Require a license image
    if (!licenseFile) {
      return true;
    }

    // Require license details with an expiration date in the future
    const licenseDetails = await getUserDriverLicense();
    return !licenseDetails || licenseDetails.expirationDate < DateTime.local();
  };

  const isInsuranceVisible = async (): Promise<boolean> => {
    const insuranceEnabled = conf.find((c) => c.variable === 'CONF_PROOF_OF_INSURANCE')?.value === '1';
    if (!insuranceEnabled) {
      return false;
    }

    // If all items are sale, we don't need insurance
    if (!isRental) {
      return false;
    }

    const docs = await getInsuranceDocuments(false);
    return docs.length <= 0;
  };

  const isOtherVisible = (otherFile?: UserDocument): boolean => {
    const otherEnabled = conf.find((c) => c.variable === 'CONF_OTHER_DOC_UPLOAD')?.value === '1';

    if (!otherEnabled) {
      return false;
    }

    return !otherFile;
  };

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      const docs = await getUserDocuments();
      const visibleDocs: UserDocumentType[] = [];

      const _showLicense = await isLicenseVisible(docs.find((d) => d.documentType === UserDocumentType.License));
      setShowLicense(_showLicense);
      if (_showLicense) visibleDocs.push(UserDocumentType.License);

      const _showInsurance = await isInsuranceVisible();
      setShowInsurance(_showInsurance);
      if (_showInsurance) visibleDocs.push(UserDocumentType.Insurance);

      const _showOther = isOtherVisible(docs.find((d) => d.documentType === UserDocumentType.OtherCheckout));
      setShowOther(_showOther);
      if (_showOther) visibleDocs.push(UserDocumentType.OtherCheckout);

      setVisibleDocuments(visibleDocs);
      setIsLoading(false);
    };

    if (customer) fetchAll();
  }, [customer]);

  return (
    <>
      {!isLoading && (
        <>
          {showLicense && (
            <CheckoutDocumentForm
              type={UserDocumentType.License}
              currentStep={currentStep}
              triggerStep={StepAction.DriversLicense}
              title="Driver's License"
              step={step++}
              isRequired
              isSubmitting={isSubmitting}
              labels={labels}
              conf={conf}
              setCurrentStep={setCurrentStep}
            />
          )}
          {showInsurance && (
            <CheckoutDocumentForm
              type={UserDocumentType.Insurance}
              currentStep={currentStep}
              triggerStep={StepAction.Insurance}
              title="Proof Of Insurance"
              step={step++}
              isRequired={
                conf.find((c) => c.variable === 'CONF_PROOF_INSURANCE_MANDATORY')?.value === '1' ||
                isInsuranceRequiredAtProductLevel
              }
              isSubmitting={isSubmitting}
              labels={labels}
              conf={conf}
              setCurrentStep={setCurrentStep}
            />
          )}
          {showOther && (
            <CheckoutDocumentForm
              type={UserDocumentType.OtherCheckout}
              currentStep={currentStep}
              triggerStep={StepAction.OtherDocument}
              title={labels?.find((l) => l.key === 'LBL_CUSTOM_DOC_UPLOAD')?.caption || 'Other Document'}
              step={step++}
              isRequired={conf.find((c) => c.variable === 'CONF_OTHER_DOC_REQUIRED')?.value === '1'}
              isSubmitting={isSubmitting}
              labels={labels}
              conf={conf}
              setCurrentStep={setCurrentStep}
            />
          )}
        </>
      )}
    </>
  );
};

const CheckoutDocumentForm = ({
  title,
  step,
  type,
  currentStep,
  triggerStep,
  isRequired = false,
  isSubmitting,
  labels,
  conf,
  setCurrentStep,
}: {
  title: string;
  step: number;
  type: UserDocumentType;
  currentStep: StepAction;
  triggerStep: StepAction;
  isRequired?: boolean;
  isSubmitting: boolean;
  labels: Label[];
  conf: Configuration[];
  setCurrentStep: (step: StepAction) => void;
}) => {
  const customer = useCustomer();
  const isCompleted = currentStep > triggerStep;
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLicenseScanRequired] = useState<boolean>(
    !!conf.find((c) => c.variable === 'CONF_IDSCAN_LICENSE_KEY')?.value,
  );

  return (
    <CheckoutStep
      title={title}
      step={step}
      contentPadding="p-0"
      isCompleted={isCompleted}
      isEditDisabled={isSubmitting}
      hideEdit={!isRequired || isLicenseScanRequired}
      onEdit={() => {
        setCurrentStep(triggerStep);
        setIsEditing(true);
      }}
    >
      {currentStep >= triggerStep && customer && (
        <>
          {type === UserDocumentType.License && (
            <LicenseWrapper
              isEditing={isEditing}
              isRequired={isRequired}
              conf={conf}
              setIsEditing={setIsEditing}
              onSubmit={() => setCurrentStep(StepAction.Insurance)}
            />
          )}
          {type === UserDocumentType.Insurance && (
            <>
              <div className="pt-4 px-3">
                <Typography type="text-sm">
                  {`${!isRequired ? 'Optional: ' : ''}${
                    labels.find((l) => l.key === 'L_CHECKOUT_PROOF_INSURANCE')?.caption || ''
                  }`}
                </Typography>
              </div>
              <CheckoutInsuranceForm
                isRequired={isRequired}
                isCompleted={isCompleted}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                setCurrentStep={setCurrentStep}
              />
            </>
          )}
          {type === UserDocumentType.OtherCheckout && (
            <>
              <div className="pt-4 px-3">
                <Typography type="text-sm">
                  {`${!isRequired ? 'Optional: ' : ''}Please upload your ${title}${
                    isRequired ? ' in order to be able to place your order request with us' : ''
                  }.`}
                </Typography>
              </div>
              <OtherDocumentForm
                isRequired={isRequired}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onSubmit={() => setCurrentStep(StepAction.Legal)}
              />
            </>
          )}
        </>
      )}
    </CheckoutStep>
  );
};

const CheckoutInsuranceForm = ({
  isRequired,
  isEditing,
  setIsEditing,
  setCurrentStep,
}: {
  isRequired: boolean;
  isCompleted: boolean;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  setCurrentStep: (step: StepAction) => void;
}) => {
  useEffect(() => {
    if (!isRequired) {
      setCurrentStep(StepAction.OtherDocument);
    }
  }, []);

  return (
    <InsuranceFormWrapper
      hideFile={false}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      wrapperClassName="py-4 px-3"
      includeEditButton={false}
      onSubmit={() => isRequired && setCurrentStep(StepAction.OtherDocument)}
    />
  );
};

const OtherDocumentForm = ({
  isRequired,
  isEditing,
  setIsEditing,
  onSubmit,
}: {
  isRequired: boolean;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  onSubmit: () => void;
}) => {
  const customer = useCustomer();
  const { getUserDocumentByType, createUpdateUserDocument } = useCustomerAPI();

  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false);
  const [document, setDocument] = useState<UserDocument>();
  const [inputFile, setInputFile] = useState<File>();

  const onCancel = () => {
    setIsEditing(false);

    if (document) {
      onSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!inputFile || !customer || isRequestInProgress) return;

    setIsRequestInProgress(true);

    try {
      const { status, msg } = await createUpdateUserDocument(
        inputFile,
        customer.id.toString(),
        DatabaseUserDocumentType.OtherCheckout.toString(),
        true,
      );

      if (status === 1) {
        await fetchDocument();
        ecommerceToast('Document uploaded successfully.', 'success');
        isRequired && onSubmit(); // If required, move to next step
        setIsEditing(false);
        setInputFile(undefined);
      } else {
        ecommerceToast(msg || 'Something went wrong.', 'error');
      }
    } catch (error: any) {
      ecommerceToast(error.message || 'Something went wrong.', 'error');
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const fetchDocument = async () => {
    const doc = await getUserDocumentByType(UserDocumentType.OtherCheckout);
    setDocument(doc);

    if (doc) {
      onSubmit();
    }
  };

  useEffect(() => {
    if (customer) fetchDocument();
  }, [customer]);

  useEffect(() => {
    if (!isRequired) onSubmit();
  }, []);

  return (
    <div>
      {isEditing ? (
        <>
          <div className="py-4 px-3">
            <Card className="max-w-md bg-grey-25 !rounded-lg p-3">
              <FileField
                className="[&>label]:block"
                value={inputFile}
                onChange={(newFile) => setInputFile(newFile as File)}
              />
            </Card>
          </div>

          <Divider />

          {isRequestInProgress ? (
            <Spinner />
          ) : (
            <div className="p-3 flex justify-between">
              <EcommerceButton buttonStyle="tertiary" onClick={onCancel}>
                Cancel
              </EcommerceButton>
              <EcommerceButton buttonStyle="primary" disabled={!inputFile} onClick={handleSubmit}>
                Save
              </EcommerceButton>
            </div>
          )}
        </>
      ) : (
        <>
          {document ? (
            <div className="py-4 px-3">
              <div className="w-full md:w-1/2 flex flex-col gap-2">
                <div className="flex justify-between">
                  <Typography type="text-sm" weight="semi-bold">
                    Document name
                  </Typography>

                  <LegacyDownloadLink
                    className="w-4/5 flex gap-3 items-center justify-end text-[var(--primary-color-500)] hover:text-[var(--primary-color-500)]"
                    href={`users/download_file/${customer!.id}/${DatabaseUserDocumentType.OtherCheckout}`}
                  >
                    <Typography type="text-sm" weight="medium">
                      <span className="text-document-overflow">{document.fileName}</span>
                    </Typography>
                    <Icon icon="Download" size="medium" stroke="var(--primary-color-500)" />
                  </LegacyDownloadLink>
                </div>

                <div className="flex justify-between">
                  <Typography type="text-sm" weight="semi-bold">
                    Uploaded at
                  </Typography>

                  <Typography type="text-sm" className="text-quipli-black-500">
                    <span>{`${DateTime.fromISO(document.createdAt, { setZone: true }).toFormat('DD t')}`}</span>
                  </Typography>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center p-3">
              <EcommerceButton
                buttonStyle="tertiary"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <Typography type="text-sm" weight="medium">
                  Add Document
                </Typography>
              </EcommerceButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
